import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Loader2, AlertCircle, FileDown, ShoppingCart, ExternalLink, Undo2, Printer,
} from 'lucide-react';
import api from '../../services/api';
import { VettoreLogo } from '../../components/portal/VettoreLogo';
import { CondizioniTrasporto } from '../../components/portal/CondizioniTrasporto';
import { condizioniDocumento } from '../../utils/condizioniTrasporto';
import { useMyDocumentoDetail } from '../../hooks/useMyDocumenti';
import { pdfErrorMessage, scaricaPdfDocumento } from '../../utils/pdfDocumento';
import { numeroDocumento } from '../../utils/numeroDocumento';
import { scontoRiga, righeConSconto } from '../../utils/scontoRiga';

/* ── helpers ── */
const fmtEur = (n: number | null) => (n === null ? 'In arrivo' : `€ ${n.toFixed(2).replace('.', ',')}`);
const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('it-IT') : '—';

const TYPE_LABELS: Record<string, string> = {
  fattura: 'Fattura',
  ddt: 'D.D.T.',
  nota_credito: 'Nota di credito',
};

const TYPE_BADGE: Record<string, string> = {
  fattura: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  ddt: 'text-[hsl(210,64%,29%)] bg-[hsl(210,40%,95%)] border-[hsl(210,64%,80%)]',
  nota_credito: 'text-amber-700 bg-amber-50 border-amber-200',
};

/* ── Condizioni, totali e spedizione, come il PDF di NTS (`/condizioni`) ── */
interface Condizioni {
  pagamento: string | null;
  banca: string | null;
  /** Codice del porto (`tm_porto`) e la sua descrizione NTS. */
  portoCodice: string | null;
  porto: string | null;
  /** `tm_acuradi`: "M", "D", "V" o null. */
  trasportoACuraDel: string | null;
  vettore: string | null;
  causale: string | null;
  aspetto: string | null;
  colli: number | null;
  peso: number | null;
  destinazione: string | null;
  totali: {
    merce: number; speseTrasporto: number; speseIncasso: number; speseImballo: number;
    imponibile: number; imposta: number; totale: number;
    iva: { aliquota: number | null; imponibile: number; imposta: number }[];
  };
  scadenze: { data: string; importo: number }[];
  spedizione: { riferimento: string | null; vettore: string | null; url: string | null };
}

const eur = (n: number) => `€ ${n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Voce({ label, value }: { label: string; value: React.ReactNode }) {
  if (value == null || value === '') return null;
  return (
    <div className="flex justify-between gap-4 text-sm py-1">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}

/* ── Section wrapper ── */
function Section({ title, children, noPad }: { title: string; children: React.ReactNode; noPad?: boolean }) {
  return (
    <div className="rounded-xl border bg-white overflow-hidden">
      <div className="px-4 py-2.5 bg-[hsl(210,40%,97%)] border-b">
        <h2 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</h2>
      </div>
      <div className={noPad ? '' : 'p-4'}>{children}</div>
    </div>
  );
}

/* ────────────────────────────────────────────── */
export default function DocumentDetailPage() {
  const { tipork, anno, serie, numdoc } = useParams<{
    tipork: string; anno: string; serie: string; numdoc: string;
  }>();
  const [downloading, setDownloading] = useState(false);

  const key = useMemo(
    () => (tipork && anno && serie != null && numdoc)
      ? { tipork, anno: Number(anno), serie, numdoc: Number(numdoc) }
      : null,
    [tipork, anno, serie, numdoc],
  );

  const { data: doc, loading, error } = useMyDocumentoDetail(key);

  // Letti dal gestionale a ogni apertura (~1,5 s): il resto della pagina
  // si mostra subito.
  const [cond, setCond] = useState<Condizioni | null>(null);
  const [condErrore, setCondErrore] = useState<string | null>(null);
  useEffect(() => {
    if (!key) return;
    setCond(null);
    setCondErrore(null);
    api.get<Condizioni>(`/api/clienti/me/documenti/${key.tipork}/${key.anno}/${encodeURIComponent(key.serie)}/${key.numdoc}/condizioni`)
      .then(r => setCond(r.data))
      .catch(e => setCondErrore(e?.response?.data?.detail ?? 'Condizioni del documento non disponibili.'));
  }, [key]);

  const downloadPdf = async () => {
    if (!doc) return;
    setDownloading(true);
    try {
      await scaricaPdfDocumento(doc.key);
    } catch (e: any) {
      alert(pdfErrorMessage(e));
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16 text-muted-foreground">
      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento documento…
    </div>
  );

  if (error || !doc) return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-800 flex items-center gap-2">
      <AlertCircle className="w-4 h-4" />
      {error ?? 'Documento non trovato.'}
      <Link to="/area-clienti/documenti" className="ml-auto underline text-xs">← Tutti i documenti</Link>
    </div>
  );

  const items = doc.items ?? [];
  const conSconto = righeConSconto(items);
  const ordineOrigine = items.find(it => it.ordineOrigine)?.ordineOrigine ?? null;
  const typeLabel = TYPE_LABELS[doc.type] ?? doc.type;
  const badgeClass = TYPE_BADGE[doc.type] ?? '';

  return (
    <div data-testid="document-detail-page" className="space-y-4">

      {/* ── Action bar ── */}
      <div className="no-print flex flex-wrap items-center gap-2">
        <Link to="/area-clienti/documenti"
          data-testid="document-back-btn"
          className="h-9 px-3 rounded-lg border bg-white text-sm font-medium inline-flex items-center gap-1.5 hover:bg-[hsl(210,40%,97%)] transition-colors">
          <ArrowLeft className="w-4 h-4" /> Tutti i documenti
        </Link>
        <div className="flex-1" />
        <button onClick={() => window.print()}
          data-testid="document-print-btn"
          className="no-print h-9 px-3 rounded-lg border bg-white text-sm font-medium inline-flex items-center gap-1.5 hover:bg-[hsl(210,40%,97%)] transition-colors">
          <Printer className="w-4 h-4" /> Stampa
        </button>
        {doc.hasPdf && (
          <button onClick={downloadPdf} disabled={downloading}
            data-testid="document-download-btn"
            className="h-9 px-4 rounded-lg bg-[hsl(210,64%,29%)] text-white text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-[hsl(210,64%,22%)] transition-colors disabled:opacity-50">
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
            Scarica PDF
          </button>
        )}
      </div>

      {/* ── Header documento ── */}
      <div className="rounded-xl border bg-white px-4 py-3 flex flex-wrap items-center gap-3">
        <div>
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Documento</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="font-mono font-bold text-lg leading-none">{numeroDocumento(doc.numero)}</span>
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${badgeClass}`}>{typeLabel}</span>
          </div>
        </div>
        <div className="h-8 w-px bg-border hidden md:block" />
        <div>
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Data</p>
          <p className="text-sm font-medium mt-0.5">{fmtDate(doc.date)}</p>
        </div>
        {(cond?.spedizione.vettore || cond?.vettore || doc.vettor) && (
          <>
            <div className="h-8 w-px bg-border hidden md:block" />
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Vettore</p>
              <p className="text-sm mt-0.5 flex items-center gap-2 flex-wrap" data-testid="document-vettore">
                <VettoreLogo nome={cond?.spedizione.vettore || cond?.vettore || doc.vettor} />
                {cond?.spedizione.riferimento && (
                  <span className="font-mono text-xs text-muted-foreground" data-testid="document-tracking-code">{cond.spedizione.riferimento}</span>
                )}
                {cond?.spedizione.url && (
                  <a href={cond.spedizione.url} target="_blank" rel="noopener noreferrer"
                    data-testid="document-tracking-link"
                    className="no-print text-[hsl(210,64%,29%)] hover:underline inline-flex items-center gap-0.5 text-xs">
                    <ExternalLink className="w-3 h-3" /> Segui la spedizione
                  </a>
                )}
              </p>
            </div>
          </>
        )}
        <div className="ml-auto text-right">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Totale documento</p>
          <p className="font-bold text-xl tabular-nums mt-0.5">{fmtEur(doc.total)}</p>
        </div>
      </div>

      {/* ── Riepilogo articoli ── */}
      {items.length > 0 && (
        <Section title="Articoli" noPad>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-[11px] text-muted-foreground uppercase tracking-wide bg-[hsl(210,40%,98%)]">
                  <th className="text-left px-4 py-2.5 font-semibold">Prodotto</th>
                  <th className="text-left px-3 py-2.5 font-semibold hidden md:table-cell">Codice</th>
                  <th className="text-right px-3 py-2.5 font-semibold hidden md:table-cell">Prezzo/Ud.</th>
                  {conSconto && <th className="text-right px-3 py-2.5 font-semibold hidden md:table-cell">Sconto</th>}
                  <th className="text-right px-3 py-2.5 font-semibold">Qtà</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Totale</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {items.map(item => (
                  <tr key={item.riga} className="hover:bg-[hsl(210,40%,99%)] transition-colors">
                    <td className="px-4 py-3 max-w-[240px]">
                      <Link to={`/prodotti/_/${item.sku}`}
                        className="font-medium hover:text-[hsl(210,64%,29%)] hover:underline leading-snug line-clamp-2">
                        {item.title}
                      </Link>
                      <div className="md:hidden mt-1 flex items-center gap-2">
                        <span className="font-mono text-[10px] text-muted-foreground">{item.sku}</span>
                        {scontoRiga(item.scont1, item.scont2) && (
                          <span className="text-[10px] text-muted-foreground">
                            {fmtEur(item.prezzoUnitario)} {scontoRiga(item.scont1, item.scont2)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 hidden md:table-cell">
                      <Link to={`/prodotti/_/${item.sku}`}
                        className="font-mono text-xs text-[hsl(210,64%,29%)] hover:underline">
                        {item.sku}
                      </Link>
                    </td>
                    <td className="px-3 py-3 text-right hidden md:table-cell font-mono tabular-nums text-muted-foreground">
                      {fmtEur(item.prezzoUnitario)}
                    </td>
                    {conSconto && (
                      <td className="px-3 py-3 text-right hidden md:table-cell font-mono tabular-nums text-muted-foreground"
                        data-testid={`doc-riga-sconto-${item.riga}`}>
                        {scontoRiga(item.scont1, item.scont2) ?? '—'}
                      </td>
                    )}
                    <td className="px-3 py-3 text-right font-mono tabular-nums">
                      {item.quantita} <span className="text-muted-foreground text-xs">{item.unmis}</span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-semibold tabular-nums">
                      {fmtEur(item.totaleRiga)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="px-4 pb-4 pt-3 border-t flex justify-end">
            <div className="w-56 space-y-1.5 text-sm">
              <div className="flex justify-between items-baseline font-bold text-base">
                <span>Totale documento</span>
                <span className="font-mono tabular-nums text-lg">{fmtEur(doc.total)}</span>
              </div>
            </div>
          </div>
        </Section>
      )}

      {items.length === 0 && (
        <Section title="Articoli">
          <p className="text-sm text-muted-foreground italic">
            Dettaglio articoli non disponibile per questo documento.
          </p>
        </Section>
      )}

      {/* ── Condizioni e totali, come sul PDF ── */}
      {condErrore && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-center gap-2" data-testid="document-condizioni-errore">
          <AlertCircle className="w-4 h-4" /> {condErrore}
        </div>
      )}
      {!cond && !condErrore && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground px-1" data-testid="document-condizioni-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Condizioni e totali dal gestionale…
        </div>
      )}
      {cond && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="document-condizioni">
          <Section title="Condizioni commerciali">
            <Voce label="Pagamento" value={cond.pagamento} />
            <Voce label="Banca d'appoggio" value={cond.banca} />
            <CondizioniTrasporto dati={condizioniDocumento(cond)} variante="voci" />
            <Voce label="Causale del trasporto" value={cond.causale} />
            <Voce label="Aspetto dei beni" value={cond.aspetto} />
            <Voce label="Colli" value={cond.colli} />
            <Voce label="Peso" value={cond.peso != null ? `${cond.peso.toLocaleString('it-IT')} kg` : null} />
            <Voce label="Destinazione" value={cond.destinazione} />
          </Section>
          <Section title="Totali">
            <Voce label="Totale netto merce" value={eur(cond.totali.merce)} />
            {cond.totali.speseTrasporto !== 0 && <Voce label="Spese di trasporto" value={eur(cond.totali.speseTrasporto)} />}
            {cond.totali.speseIncasso !== 0 && <Voce label="Spese di incasso" value={eur(cond.totali.speseIncasso)} />}
            {cond.totali.speseImballo !== 0 && <Voce label="Spese di imballo" value={eur(cond.totali.speseImballo)} />}
            <Voce label="Totale imponibile" value={eur(cond.totali.imponibile)} />
            {cond.totali.iva.map((r, i) => (
              <Voce key={i} label={r.aliquota != null ? `IVA ${r.aliquota}%` : 'IVA'} value={eur(r.imposta)} />
            ))}
            <div className="flex justify-between gap-4 pt-2 mt-1 border-t font-bold" data-testid="document-totale">
              <span>Totale documento</span>
              <span className="font-mono tabular-nums">{eur(cond.totali.totale)}</span>
            </div>
            {cond.scadenze.length > 0 && (
              <div className="mt-3 pt-2 border-t">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Scadenze</p>
                {cond.scadenze.map(sc => (
                  <Voce key={sc.data} label={new Date(sc.data + 'T00:00:00').toLocaleDateString('it-IT')} value={eur(sc.importo)} />
                ))}
              </div>
            )}
          </Section>
        </div>
      )}

      {/* ── Info aggiuntive ── */}
      <div className="grid md:grid-cols-2 gap-4">
        {ordineOrigine && (() => {
          // Una nota di credito viene da un Ordine RMA: si apre dalla pratica.
          const reso = ordineOrigine.serie.trim() === 'RMA';
          const numero = `${ordineOrigine.serie.trim()}/${ordineOrigine.anno}/${String(ordineOrigine.numdoc).padStart(5, '0')}`;
          const to = reso
            ? (ordineOrigine.rma ? `/area-clienti/rma/${encodeURIComponent(ordineOrigine.rma)}/ordine` : null)
            : `/area-clienti/ordini/${ordineOrigine.tipork}/${ordineOrigine.anno}/${encodeURIComponent(ordineOrigine.serie)}/${ordineOrigine.numdoc}`;
          const Icona = reso ? Undo2 : ShoppingCart;
          return (
            <Section title={reso ? 'Ordine di reso' : 'Ordine di origine'}>
              <div className="flex items-center gap-2">
                <Icona className="w-4 h-4 text-muted-foreground flex-none" />
                {to ? (
                  <Link to={to} data-testid="document-order-origin-link"
                    className="text-sm text-[hsl(210,64%,29%)] hover:underline font-mono">
                    {numero}
                  </Link>
                ) : (
                  <span className="text-sm font-mono" data-testid="document-order-origin">{numero}</span>
                )}
              </div>
            </Section>
          );
        })()}
      </div>

    </div>
  );
}
